import calculateFilterCounts, {
  defaultValues as defaultFilterCounts,
} from '@/app/features/reports/utils/calculateFilterCounts'
import { ReportTestItem } from '@/app/features/reports/utils/entities';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FilterCounts,
  filterNames,
  Filters,
  FilterTypes,
  SelectedFilters,
  ExclusionReason,
} from '../types/filters';
import {
  createCompositeFilter,
  createElementNameFilter,
  createElementIdFilter,
  createFilter,
  createResultFilter,
  createStatusFilter,
  createTestSuiteFilter,
  Filter,
  filterByLocation,
  createElementFilter,
  createExclusionFilter,
  createSearchFilter,
} from './filters';
import convertTestsToSpaces from './convertTestsToSpaces';
import convertTestsToTestSuiteFilters from './convertTestsToTestSuiteFilters';
import convertTestsToRatingFilters from './convertTestsToRatingFilters';
import convertSinglePropertyToFilters from './convertSinglePropertyToFilters';
import { ReportEntity } from 'store/src/lib/services/types';
import { FilterState, getFacetCounts } from './facets';
import { convertTestsToElementFacets } from './convertTestsToElementFacets';
import convertTestsToExclusionFacets from '@/app/features/reports/utils/convertTestsToExclusionFacets';


// Define fixed filters for rapidQA
// const rapidQAExclusionReasons = new Set<ExclusionReason>(['manuallyexcluded', 'status', 'unmatchedsegments', 'missingdata']);
const rapidQAExclusionReasons = new Set<ExclusionReason>(['status', 'unmatchedsegments', 'missingdata']);
const rapidQAStatuses = new Set(['draft', 'published', 'qa']);

const filterItems = (
  items: ReportTestItem[],
  selectedFilters: SelectedFilters,
  filterInput: string
) => {
  if (!items?.length) return [];

  const activeFilters: Filter[] = [];

  // Status filter - Check length and handle array
  if (selectedFilters.status?.length > 0) {
    const statusSet = new Set(
      selectedFilters.status.flatMap(s => s.split(',')).map(s => s.toLowerCase())
    );
    if (statusSet.size > 0) activeFilters.push(createStatusFilter(statusSet));
  }

  // Element name filter - Check length and handle array
  if (selectedFilters.elementName?.length > 0) {
    const elementSet = new Set(
      selectedFilters.elementName.flatMap(s => s.split(',')).map(s => s.toLowerCase())
    );
    if (elementSet.size > 0) activeFilters.push(createElementNameFilter(elementSet));
  }

  // Element id filter - Check length and handle array
  if (selectedFilters.elementId?.length > 0) {
    const elementSet = new Set(
      selectedFilters.elementId.flatMap(s => s.split(',')).map(s => s.toLowerCase())
    );
     if (elementSet.size > 0) activeFilters.push(createElementIdFilter(elementSet));
  }

  // Test suite filter - Check length and handle array
  if (selectedFilters.testSuite?.length > 0) {
    const suiteSet = new Set(selectedFilters.testSuite.flatMap(s => s.split(',')));
    if (suiteSet.size > 0) activeFilters.push(createTestSuiteFilter(suiteSet));
  }

  // Rating type filter - Check length and handle array
  if (selectedFilters.rating?.length > 0) {
    const resultSet = new Set(selectedFilters.rating.flatMap(s => s.split(',')));
    if (resultSet.size > 0) activeFilters.push(createResultFilter(resultSet));
  }

  // Element filter - Check length and handle array
  if (selectedFilters.element?.length > 0) {
    const elementSet = new Set(
      selectedFilters.element.flatMap(s => s.split(',')).map(s => s.toLowerCase())
    );
    if (elementSet.size > 0) activeFilters.push(createElementFilter(elementSet));
  }

  // Excluded filter - Check length and handle array
  if (selectedFilters.excluded?.length > 0) {
    const exclusionSet = new Set(
      selectedFilters.excluded.flatMap(s => s.split(',')).map(s => s.toLowerCase())
    );
    if (exclusionSet.size > 0) activeFilters.push(createExclusionFilter(exclusionSet));
  }

  // Location filters - Check length and handle array
  if (selectedFilters.space?.length > 0) {
    const locations = selectedFilters.space.flatMap(s => s.split(','));
    if (locations.length > 0) {
        const locationFilters = locations.map(location => filterByLocation(location));
        activeFilters.push(
            createFilter({
                matches: (item) =>
                    locationFilters.some((filter) => filter.matches(item)),
            })
        );
    }
  }

  if (filterInput) {
    activeFilters.push(createSearchFilter(filterInput));
  }

  // If no filters are active, return all items
  if (activeFilters.length === 0) {
    return items;
  }

  // Create and apply composite filter
  const compositeFilter = createCompositeFilter(activeFilters);
  return items.filter((item) => compositeFilter.matches(item));
};

const defaultValues: SelectedFilters = filterNames.reduce((acc, filterName) => {
  acc[filterName] = [];
  return acc;
}, {} as SelectedFilters);

export { defaultSelectedFilters as defaultInitialFilters } from '../types/filters';

const useReportItemFilters = (
  testItems: ReportTestItem[],
  report: ReportEntity,
  selectedFilters: SelectedFilters,
  setSelectedFilters: (filters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters)) => void,
  viewId: string
) => {
  const location = useLocation();
  const [filterInput, setFilterInput] = useState<string>('');

  // Don't apply text search on Rapid QA page, but preserve it for when returning to results
  const isRapidQARoute = location.pathname.includes('/rapidqa');
  const effectiveFilterInput = isRapidQARoute ? '' : filterInput;

  const userFilteredItems = useMemo(
    () => filterItems(testItems, selectedFilters, effectiveFilterInput),
    [testItems, selectedFilters, effectiveFilterInput]
  );

  const filteredData = useMemo(() => {
    if (!viewId) return userFilteredItems; // Default: no fixed filters

    switch (viewId) {
      case 'summary':
        return userFilteredItems.filter(item => item.exclusionReason === 'included');
      case 'rapidqa':
        return userFilteredItems.filter(item => 
          item.exclusionReason && rapidQAExclusionReasons.has(item.exclusionReason.toLowerCase() as ExclusionReason) &&
          item.status && rapidQAStatuses.has(item.status.toLowerCase()) 
        );
      // Add cases for other viewIds if needed
      default: // E.g., 'results'
        return userFilteredItems;
    }
  }, [userFilteredItems, viewId]);

  const state: FilterState = useMemo(() => {
    if (!testItems?.length) {
      // Revert to previous default structure
      return {
        filteredItems: [],
        allItems: [],
        counts: {
          ...defaultFilterCounts,
        },
      };
    }

    // Revert to calculating counts based on both sets of items
    return {
      filteredItems: filteredData,
      allItems: testItems,
      counts: calculateFilterCounts(filteredData, testItems),
    };
  }, [filteredData, testItems]);

  const updateFilters = useCallback((newFilters: SelectedFilters | ((prev: SelectedFilters) => SelectedFilters)) => {
    setSelectedFilters(newFilters);
  }, [setSelectedFilters]);

  const removeFilter = useCallback(
    (category: FilterTypes, filterToRemove: string) => {
      updateFilters((prevSelectedFilters) => {
        const currentFilters = prevSelectedFilters[category] ?? [];
        const newFilters = currentFilters.filter(f => f !== filterToRemove);
        return {
          ...prevSelectedFilters,
          [category]: newFilters,
        };
      });
    },
    [updateFilters] // Dependency is only updateFilters
  );

  const clearFilters = useCallback(() => {
    updateFilters(defaultValues); // Reset to default empty arrays
    setFilterInput('');
  }, [updateFilters]);

  const filtersActive = useMemo(
    () =>
      Object.values(selectedFilters).some(
        (filterGroup) => filterGroup.length > 0
      ),
    [selectedFilters]
  );

  //@ts-expect-error
  const filters: Filters = useMemo(() => {
    if (!testItems?.length || !report) return { ...defaultValues };

    // Use final filteredData for generating facets
    const spaces = convertTestsToSpaces(
      filteredData, 
      testItems,
      getFacetCounts('space', state)
    );

    const testSuiteFilters = convertTestsToTestSuiteFilters(testItems);

    const ratingFilters = convertTestsToRatingFilters(
      filteredData, 
      testItems,
      report?.testSuites,
      getFacetCounts('rating', state)
    );

    const elementFilters = convertTestsToElementFacets(
      filteredData, 
      testItems,
      getFacetCounts('element', state)
    );

    const exclusionFilters = convertTestsToExclusionFacets(
      filteredData, 
      testItems,
      getFacetCounts('excluded', state)
    );

    const statusFilters = convertSinglePropertyToFilters(testItems, {
      key: 'status',
      idProperty: 'status',
      nameProperty: 'status',
      customOrder: ['published', 'qa', 'draft', 'archived'],
      displayValue: (value) =>
        ({
          published: 'Final',
          qa: 'QA',
          draft: 'Draft',
          archived: 'Archived',
        }[value?.toLowerCase()] || value),
    });

    const elementNameFilters = convertSinglePropertyToFilters(testItems, {
      key: 'elementName',
      idProperty: 'element_name',
      nameProperty: 'element_name',
      emptyTitle: '(No Element Name)',
    });

    const elementId = convertSinglePropertyToFilters(testItems, {
      key: 'elementId',
      idProperty: 'element_id',
      nameProperty: 'element_id',
      emptyTitle: '(No Element ID)',
    });

    return {
      space: spaces,
      testSuite: testSuiteFilters,
      rating: ratingFilters,
      status: statusFilters,
      elementName: elementNameFilters,
      elementId: elementId,
      element: elementFilters,
      excluded: exclusionFilters,
    };
  }, [testItems, report, filteredData, state]);

  return {
    selectedFilters,
    facets: filters,
    filteredData,
    filteredCounts: state.counts,
    unfilteredCounts: state.counts,
    filtersActive,
    setSelectedFilters,
    updateFilters,
    removeFilter,
    clearFilters,
    filterInput,
    setFilterInput,
  };
};

export default useReportItemFilters;
